import { Box, Grid, SxProps, useMediaQuery, useTheme } from "@mui/material";
import ConfirmationDetailCard from "../../../../Shared/ConfirmationDetailCard";
import { CalendarDays, Gift, IdCardLanyard, LocateFixed, Receipt, UserRound, UsersRound } from "lucide-react";
import { BookingStatus } from "../../../bookingSlice.types";
import { selectBooking, selectBookingType, setNotes } from "../../../bookingSlice";
import ConfirmationNotesCard from "../../../../Shared/ConfirmationNotesCard";
import SuccessSection from "../SuccessSection";
import { useSessionBookingData } from "../useSessionBookingData";
import { useClientDiscount } from "../useClientDiscount";
import { useClientPortalDispatch, useClientPortalSelector } from "../../../../../redux";
import { useClientPortalConfigContext } from "context/ClientPortalConfig.ctx";
import { amountToCurrency } from "utils/localeHelper";
import { useMemo } from "react";
import ConfirmationSubmit from "./ConfirmationSubmit";

import styles from "./ConfirmationNoPayment.module.css";
import { ConfirmBookingFunction } from "../../../hooks/useBookingConfirmation";
import { AppointmentBookingClientModel } from "types/generated/models";
import ConfirmationAppointmentForCard from "../ConfirmationAppointmentForCard";
import { BookingType } from "types/appointmentBooking";
import { getGroupBookingLabel } from "../../../../../utils/groupBookingLabel";

export interface ConfirmationNoPaymentProps {
    confirmBooking: ConfirmBookingFunction;
    isLoading: boolean;
    bookedSuccessfully: boolean;
    shouldShowContactsDropdown: boolean;
    relatedClients: AppointmentBookingClientModel[];
}

export default function ConfirmationNoPayment({
    confirmBooking,
    isLoading,
    bookedSuccessfully,
    shouldShowContactsDropdown,
    relatedClients,
}: ConfirmationNoPaymentProps) {
    const { session, confirmation } = useClientPortalSelector(selectBooking);
    const dispatch = useClientPortalDispatch();

    const {
        locationContent,
        serviceNameContent,
        groupContent,
        providerContent,
        whenContent,
        whenTimeZoneContent,
        clientContent,
        rawServicePrice,
    } = useSessionBookingData();
    const { discountContent, applyDiscountToAmount } = useClientDiscount();
    const {
        clientPortalConfig: { currencySymbol, groupBookingLabel },
    } = useClientPortalConfigContext();
    const configuredGroupBookingLabel = getGroupBookingLabel(groupBookingLabel);

    const discountedPrice = rawServicePrice ? applyDiscountToAmount(rawServicePrice) : null;
    const servicePriceContent = discountedPrice ? amountToCurrency(discountedPrice, currencySymbol) : null;
    const bookingType = useClientPortalSelector(selectBookingType);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const isBookingCompleted = session.bookingStatus === BookingStatus.BOOKED;
    const completedData = session.completedBookingData;

    const notes =
        isBookingCompleted && completedData ? completedData.confirmation?.notes || "" : confirmation.data?.notes || "";

    const handleNotesChange = (newNotes: string) => {
        dispatch(setNotes(newNotes));
    };

    const rightColumnStyles: SxProps = useMemo(
        () => ({
            display: "flex",
            flexDirection: "column",
            minHeight: isMobile ? "auto" : "200px",
            position: "relative",
        }),
        [isMobile]
    );

    return (
        <>
            <Box className={styles.confirmationNoPaymentWrapper}>
                <Grid container spacing={3} sx={{ width: "100%" }}>
                    <Grid
                        sx={{ width: "100%" }}
                        size={{
                            xs: 12,
                            md: 6,
                        }}
                    >
                        <Box className={styles.leftColumn}>
                            <ConfirmationDetailCard title="Location" content={locationContent} icon={LocateFixed} />
                            <ConfirmationDetailCard title="Provider" content={providerContent} icon={IdCardLanyard} />
                            {bookedSuccessfully && (
                                <>
                                    {bookingType === BookingType.CLIENT && (
                                        <ConfirmationDetailCard
                                            title="Service"
                                            content={serviceNameContent}
                                            icon={UserRound}
                                        />
                                    )}
                                    {bookingType === BookingType.CLASS && (
                                        <ConfirmationDetailCard
                                            title={configuredGroupBookingLabel}
                                            content={groupContent}
                                            icon={UsersRound}
                                        />
                                    )}
                                    <ConfirmationDetailCard
                                        title="When"
                                        content={whenContent}
                                        contentSuffix={whenTimeZoneContent}
                                        icon={CalendarDays}
                                    />
                                    {servicePriceContent && (
                                        <ConfirmationDetailCard
                                            title="Amount"
                                            content={servicePriceContent}
                                            icon={Receipt}
                                            subContent={discountContent}
                                        />
                                    )}
                                    {shouldShowContactsDropdown && (
                                        <ConfirmationDetailCard
                                            title="Appointment for:"
                                            content={clientContent}
                                            icon={Gift}
                                        />
                                    )}
                                </>
                            )}

                            <ConfirmationNotesCard
                                notes={notes}
                                isBookingCompleted={isBookingCompleted}
                                onNotesChange={handleNotesChange}
                            />
                        </Box>
                    </Grid>

                    <Grid
                        sx={{ width: "100%" }}
                        size={{
                            xs: 12,
                            md: 6,
                        }}
                    >
                        {bookedSuccessfully ? (
                            <SuccessSection
                                paymentData={completedData?.payment}
                                appointmentId={completedData?.availability?.timeEntry?.appointmentId}
                            />
                        ) : (
                            <Box sx={rightColumnStyles} data-test-id="section_confirmation_details_right_column">
                                {bookingType === BookingType.CLIENT && (
                                    <ConfirmationDetailCard
                                        title="Service"
                                        content={serviceNameContent}
                                        icon={UserRound}
                                    />
                                )}
                                {bookingType === BookingType.CLASS && (
                                    <ConfirmationDetailCard
                                        title={configuredGroupBookingLabel}
                                        content={groupContent}
                                        icon={UsersRound}
                                    />
                                )}
                                <ConfirmationDetailCard
                                    title="When"
                                    content={whenContent}
                                    contentSuffix={whenTimeZoneContent}
                                    icon={CalendarDays}
                                />
                                {servicePriceContent && (
                                    <ConfirmationDetailCard
                                        title="Amount"
                                        content={servicePriceContent}
                                        icon={Receipt}
                                        subContent={discountContent}
                                    />
                                )}
                                {shouldShowContactsDropdown && (
                                    <ConfirmationAppointmentForCard relatedClients={relatedClients} />
                                )}
                            </Box>
                        )}
                    </Grid>
                </Grid>
            </Box>
            {!bookedSuccessfully && <ConfirmationSubmit confirmBooking={confirmBooking} isLoading={isLoading} />}
        </>
    );
}
